import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { FileText, Plus, Trash2, PlusCircle, LogOut, LayoutDashboard, Calendar } from 'lucide-react';

export default function AdminExams() {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showQuestionBankDialog, setShowQuestionBankDialog] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [categories, setCategories] = useState([]);
  const [questionBank, setQuestionBank] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [examForm, setExamForm] = useState({
    title: '',
    duration_minutes: 60,
    start_time: '',
    end_time: ''
  });
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    fetchExams();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (showQuestionBankDialog) {
      fetchQuestionBank();
    }
  }, [showQuestionBankDialog, selectedCategory]);

  const fetchExams = async () => {
    try {
      const response = await axios.get(`${API}/admin/exams`);
      setExams(response.data);
    } catch (error) {
      toast.error('Failed to fetch exams');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  const fetchQuestionBank = async () => {
    try {
      const url = selectedCategory === 'all' 
        ? `${API}/admin/question-bank`
        : `${API}/admin/question-bank?category_id=${selectedCategory}`;
      const response = await axios.get(url);
      setQuestionBank(response.data);
    } catch (error) {
      toast.error('Failed to fetch question bank');
    }
  };

  const handleCreateExam = async (e) => {
    e.preventDefault();
    try {
      const examData = {
        ...examForm,
        start_time: new Date(examForm.start_time).toISOString(),
        end_time: new Date(examForm.end_time).toISOString()
      };
      await axios.post(`${API}/admin/exam`, examData);
      toast.success('Exam created successfully');
      setShowCreateDialog(false);
      setExamForm({ title: '', duration_minutes: 60, start_time: '', end_time: '' });
      fetchExams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create exam');
    }
  };

  const handleAddQuestionsFromBank = async () => {
    if (selectedQuestions.length === 0) {
      toast.error('Please select at least one question');
      return;
    }

    try {
      await axios.post(`${API}/admin/exam/${selectedExam.id}/add-from-bank`, selectedQuestions);
      toast.success(`Added ${selectedQuestions.length} question(s) successfully`);
      setShowQuestionBankDialog(false);
      setSelectedQuestions([]);
      setSelectedCategory('all');
      fetchExams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add questions');
    }
  };

  const toggleQuestionSelection = (questionId) => {
    setSelectedQuestions(prev => 
      prev.includes(questionId) 
        ? prev.filter(id => id !== questionId)
        : [...prev, questionId]
    );
  };

  const handleDeleteQuestion = async (examId, questionId) => {
    try {
      await axios.delete(`${API}/admin/exam/${examId}/question/${questionId}`);
      toast.success('Question deleted successfully');
      fetchExams();
    } catch (error) {
      toast.error('Failed to delete question');
    }
  };

  const handleDeleteExam = async (examId) => {
    if (!window.confirm('Are you sure you want to delete this exam?')) return;
    try {
      await axios.delete(`${API}/admin/exam/${examId}`);
      toast.success('Exam deleted successfully');
      fetchExams();
    } catch (error) {
      toast.error('Failed to delete exam');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    toast.success('Logged out successfully');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-sky-100 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LayoutDashboard className="h-7 w-7 text-sky-600" />
            <div>
              <h1 className="text-xl font-bold text-gray-900" style={{fontFamily: 'Space Grotesk, sans-serif'}}>ESAP Admin</h1>
              <p className="text-sm text-gray-500">Exam Management</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-gray-900">{user?.name}</p>
              <p className="text-xs text-gray-500">{user?.email}</p>
            </div>
            <Button variant="outline" onClick={handleLogout} data-testid="admin-logout-btn" className="border-sky-200">
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex gap-6 py-3">
            <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} data-testid="nav-dashboard">
              Dashboard
            </Button>
            <Button variant="ghost" onClick={() => navigate('/admin/students')} data-testid="nav-students">
              Students
            </Button>
            <Button variant="ghost" className="text-sky-600" onClick={() => navigate('/admin/exams')} data-testid="nav-exams">
              Exams
            </Button>
            <Button variant="ghost" onClick={() => navigate('/admin/categories')} data-testid="nav-categories">
              Categories
            </Button>
            <Button variant="ghost" onClick={() => navigate('/admin/question-bank')} data-testid="nav-question-bank">
              Question Bank
            </Button>
            <Button variant="ghost" onClick={() => navigate('/admin/results')} data-testid="nav-results">
              Results
            </Button>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Exams</h2>
            <p className="text-gray-600">Create and manage examinations</p>
          </div>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-exam-btn" className="bg-sky-600 hover:bg-sky-700">
            <Plus className="h-4 w-4 mr-2" />
            Create Exam
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="spinner mx-auto mb-4"></div>
            <p className="text-gray-500">Loading exams...</p>
          </div>
        ) : exams.length === 0 ? (
          <Card className="border-sky-100">
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No exams yet. Create your first exam to get started.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {exams.map((exam) => (
              <Card key={exam.id} className="border-sky-100 hover:shadow-md transition-shadow" data-testid={`exam-card-${exam.id}`}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle className="text-xl">{exam.title}</CardTitle>
                      <div className="flex gap-4 mt-2 text-sm text-gray-600">
                        <span>Duration: {exam.duration_minutes} min</span>
                        <span>Total Marks: {exam.total_marks}</span>
                        <span>Questions: {exam.questions?.length || 0}</span>
                      </div>
                      <div className="flex gap-2 mt-2">
                        <Badge className="bg-blue-100 text-blue-700">
                          {new Date(exam.start_time).toLocaleDateString()}
                        </Badge>
                        <Badge className={exam.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                          {exam.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedExam(exam);
                          setSelectedQuestions([]);
                          setShowQuestionBankDialog(true);
                        }}
                        data-testid={`add-from-bank-${exam.id}`}
                        className="border-sky-200"
                      >
                        <PlusCircle className="h-4 w-4 mr-1" />
                        Add from Bank
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteExam(exam.id)}
                        data-testid={`delete-exam-${exam.id}`}
                        className="border-red-200 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                {exam.questions && exam.questions.length > 0 && (
                  <CardContent>
                    <div className="space-y-2">
                      <h4 className="font-semibold text-sm text-gray-700">Questions:</h4>
                      {exam.questions.map((q, idx) => (
                        <div key={q.id} className="flex justify-between items-start p-3 bg-gray-50 rounded border border-gray-200" data-testid={`question-${idx}`}>
                          <div className="flex-1">
                            <p className="text-sm font-medium">{idx + 1}. {q.question_text}</p>
                            <div className="flex gap-3 mt-1 text-xs text-gray-600">
                              <span>Type: {q.question_type.replace('_', ' ')}</span>
                              <span>Marks: {q.marks}</span>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteQuestion(exam.id, q.id)}
                            data-testid={`delete-question-${idx}`}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Create Exam Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl" data-testid="create-exam-dialog">
          <DialogHeader>
            <DialogTitle>Create New Exam</DialogTitle>
            <DialogDescription>Enter exam details to create a new examination</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateExam}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="title">Exam Title</Label>
                <Input
                  id="title"
                  value={examForm.title}
                  onChange={(e) => setExamForm({ ...examForm, title: e.target.value })}
                  required
                  data-testid="exam-title-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="duration">Duration (minutes)</Label>
                  <Input
                    id="duration"
                    type="number"
                    value={examForm.duration_minutes}
                    onChange={(e) => setExamForm({ ...examForm, duration_minutes: parseInt(e.target.value) })}
                    required
                    min="1"
                    data-testid="exam-duration-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start-time">Start Date & Time</Label>
                  <Input
                    id="start-time"
                    type="datetime-local"
                    value={examForm.start_time}
                    onChange={(e) => setExamForm({ ...examForm, start_time: e.target.value })}
                    required
                    data-testid="exam-start-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end-time">End Date & Time</Label>
                  <Input
                    id="end-time"
                    type="datetime-local"
                    value={examForm.end_time}
                    onChange={(e) => setExamForm({ ...examForm, end_time: e.target.value })}
                    required
                    data-testid="exam-end-input"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-create-exam" className="bg-sky-600 hover:bg-sky-700">
                Create Exam
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add from Question Bank Dialog */}
      <Dialog open={showQuestionBankDialog} onOpenChange={setShowQuestionBankDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="question-bank-dialog">
          <DialogHeader>
            <DialogTitle>Add Questions from Bank to {selectedExam?.title}</DialogTitle>
            <DialogDescription>Select questions from your question bank</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-4">
              <Label>Filter by Category:</Label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Badge variant="outline">{selectedQuestions.length} selected</Badge>
            </div>

            {questionBank.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No questions in the bank. Please add questions to the question bank first.
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {questionBank.map((question) => (
                  <div
                    key={question.id}
                    className={`border rounded-lg p-4 cursor-pointer transition-all ${
                      selectedQuestions.includes(question.id)
                        ? 'border-sky-500 bg-sky-50'
                        : 'border-gray-200 hover:border-sky-300'
                    }`}
                    onClick={() => toggleQuestionSelection(question.id)}
                    data-testid={`bank-question-${question.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selectedQuestions.includes(question.id)}
                        onChange={() => toggleQuestionSelection(question.id)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 mb-2">{question.question_text}</p>
                        <div className="flex gap-2 flex-wrap">
                          <Badge className="bg-blue-100 text-blue-700">
                            {question.question_type.replace('_', ' ')}
                          </Badge>
                          {question.category_name && (
                            <Badge className="bg-purple-100 text-purple-700">
                              {question.category_name}
                            </Badge>
                          )}
                          <Badge className="bg-green-100 text-green-700">
                            {question.marks} {question.marks === 1 ? 'mark' : 'marks'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => {
                setShowQuestionBankDialog(false);
                setSelectedQuestions([]);
                setSelectedCategory('all');
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAddQuestionsFromBank}
              disabled={selectedQuestions.length === 0}
              className="bg-sky-600 hover:bg-sky-700"
              data-testid="add-selected-questions-btn"
            >
              Add {selectedQuestions.length} Question{selectedQuestions.length !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}